import { motion, AnimatePresence } from 'motion/react';

interface ModalTheme3DProps {
  children: React.ReactNode;
}

/**
 * Wraps the modal layer for 3D mode, providing a cohesive dark glass theme
 * that sits over the 3D canvas. All existing modals render inside this wrapper
 * and inherit the 3D game's visual style via CSS custom properties.
 */
export default function ModalTheme3D({ children }: ModalTheme3DProps) {
  return (
    <div
      className="modal-theme-3d"
      style={{
        // CSS custom properties that cascade into child modals
        ['--modal-bg' as string]: 'rgba(8, 8, 25, 0.88)',
        ['--modal-border' as string]: 'rgba(255, 255, 255, 0.1)',
        ['--modal-backdrop' as string]: 'rgba(0, 0, 0, 0.6)',
        ['--modal-blur' as string]: '16px',
        position: 'relative',
        zIndex: 6000,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Full-screen 3D overlay panel for custom 3D-specific screens.
 * Use this for screens like level select, shop, etc. that need
 * to feel fully integrated with the 3D world.
 */
export function OverlayPanel3D({
  isOpen,
  onClose,
  title,
  children,
  accentColor = '#4ecdc4',
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  accentColor?: string;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 6000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(10, 10, 30, 0.9)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 18,
              width: '92%',
              maxWidth: 440,
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 20px 14px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <span style={{
                color: '#fff',
                fontSize: 14,
                fontWeight: 800,
                fontFamily: '"Press Start 2P", monospace',
                letterSpacing: '0.5px',
              }}>
                {title}
              </span>
              <motion.button
                whileTap={{ scale: 0.85 }}
                onClick={onClose}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  cursor: 'pointer',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                x
              </motion.button>
            </div>

            {/* Body */}
            <div style={{
              padding: 20,
              overflowY: 'auto',
              flex: 1,
            }}>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
